import { useSessionMetadataValues } from "contexts/SessionMetadata/context";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { SliceZone } from "@prismicio/react";
import { toast } from "react-toastify";
import { Layout } from "components/Layout";
import { Button } from "components/Form/Button";
import { OrbQRCode } from "components/OrbQRCode";
import { StepDetails } from "components/TaskInstructionSlices/StepDetails";
import { taskInstructionSliceComponents } from "components/TaskInstructionSlices";
import { StepProgressOverview } from "components/StepProgressOverview";
import { DisclosureCard } from "components/DisclosureCard";
import { RtmsSectionDetails } from "components/RtmsSectionDetails";
import { CollectionDetailsContext } from "contexts/CollectionDetails";
import {
  StepCaptureData,
  StepDetailsData,
  useMultiStepCapture,
} from "contexts/MultiStepCaptureContext";
import { useTaskDefinition } from "hooks/useTaskDefinition";
import { useOrbQrContent } from "hooks/useOrbQrContent";
import { usePrivacyModal } from "hooks/usePrivacyModal";
import {
  getOrbStepDetailFields,
  ResolvedWorkTask,
  ResolvedWorkTaskStep,
} from "lib/work-tasks";
import {
  hasChangedTaskPositions,
  getStoredWorkTaskSharedDetails,
  getWorkTaskStepRoute,
  getWorkTaskStepRouteState,
  getWorkTaskStepSchemaFingerprint,
  isWorkTaskStepComplete,
} from "lib/work-task-navigation";
import {
  buildRtmsSequenceQr,
  getRtmsAttempt,
  getRtmsFieldValues,
  getRtmsRun,
  getRtmsRunPlan,
  getRtmsSection,
  getRtmsSectionStart,
  getRtmsSequenceValues,
  getRtmsStepFingerprint,
  getWorkTaskSections,
  RTMS_RUN_STEP,
  validateRtmsValues,
} from "lib/rtms-sequence";
import { getOrbDetailPairs } from "lib/orb-qr";
import {
  getCompletedTaskSteps,
  saveCompletedTaskSteps,
} from "lib/task-progress";
import { AppRoutes, PrismicCollectionDetailField } from "types";
import { getTaskNavigationSteps } from "lib/task-navigation-qr";

export function RtmsSequence() {
  const location = useLocation();
  const navigate = useNavigate();
  const { config } = useContext(CollectionDetailsContext);
  const { accepted } = usePrivacyModal();
  const context = useMultiStepCapture();
  const { setCurrentTaskId, addStepDetails } = context;
  const taskId = location.state?.taskId as string | undefined;
  const [reload, setReload] = useState(0);
  const [setupError, setSetupError] = useState("");
  const initializing = useRef("");
  const definition = useTaskDefinition({
    taskId,
    workTaskDocumentId: taskId
      ? config?.tasks[taskId]?.workTaskDocumentId
      : undefined,
    enabled: config !== undefined,
    reloadKey: reload,
  });
  const flow = definition.workTask;
  const positionChanged =
    flow &&
    context.currentTaskId === taskId &&
    !context.isLoadingSteps &&
    hasChangedTaskPositions(flow, [
      ...context.stepCaptures,
      ...context.stepDetails,
    ]);
  const requestedStep = flow?.steps.find(
    (item) => item.stepNumber === Number(location.state?.stepNumber)
  );
  // Old links and the capture overview can point inside a single QR section.
  const step =
    flow && requestedStep
      ? getRtmsSectionStart(flow, requestedStep)
      : undefined;
  const shared = useMemo(
    () =>
      flow ? getStoredWorkTaskSharedDetails(flow, context.stepCaptures) : null,
    [context.stepCaptures, flow]
  );
  const identity = useOrbQrContent({
    extension: 0,
    storedDetails: null,
    includeDetails: false,
    detailsLoaded: !definition.loading,
  });
  const plan = useMemo(() => (flow ? getRtmsRunPlan(flow) : ""), [flow]);
  const identityKey = JSON.stringify([
    identity.userId,
    identity.sessionId,
    identity.collectionId,
    taskId,
    getOrbDetailPairs(shared),
  ]);
  const run = getRtmsRun(context.stepDetails);
  const runReady = Boolean(
    run && run.identity === identityKey && run.plan === plan
  );
  const restored = context.currentTaskId === taskId && !context.isLoadingSteps;

  useEffect(() => {
    if (taskId) setCurrentTaskId(taskId);
    if (!taskId || accepted === false) navigate(AppRoutes.home);
  }, [accepted, setCurrentTaskId, navigate, taskId]);

  useEffect(() => {
    if (
      !restored ||
      positionChanged ||
      accepted !== true ||
      !flow ||
      !step?.rtmsSequence ||
      !identity.userId ||
      !identity.sessionId ||
      !identity.collectionId ||
      runReady
    )
      return;
    const key = identityKey + plan + reload;
    if (initializing.current === key) return;
    initializing.current = key;
    setSetupError("");
    Promise.resolve()
      .then(() =>
        addStepDetails({
          stepNumber: RTMS_RUN_STEP,
          stepDetails: [],
          rtmsRun: {
            id: crypto.randomUUID(),
            identity: identityKey,
            plan,
          },
        })
      )
      .catch(() =>
        setSetupError("Sequence progress could not be saved. Please retry.")
      );
  }, [
    addStepDetails,
    accepted,
    flow,
    identity.collectionId,
    identity.sessionId,
    identity.userId,
    identityKey,
    plan,
    restored,
    positionChanged,
    runReady,
    step,
    reload,
  ]);

  const error =
    (positionChanged
      ? "Task positions changed since your progress was saved. Contact the collection manager before continuing."
      : "") ||
    definition.error ||
    identity.error ||
    setupError ||
    (identity.userId && identity.sessionId && !identity.collectionId
      ? "The collection identifier is missing. Please return to the campaign and sign in again."
      : "");
  if (error)
    return (
      <Layout>
        <p role="alert">
          {error instanceof Error ? error.message : String(error)}
        </p>
        <Button onClick={() => setReload((value) => value + 1)}>Retry</Button>
        <Button onClick={() => navigate(AppRoutes.home)}>Back home</Button>
      </Layout>
    );
  if (
    definition.loading ||
    !restored ||
    !identity.userId ||
    !identity.sessionId ||
    !identity.collectionId
  ) {
    return (
      <Layout>
        <p>Loading RTMS sequence...</p>
      </Layout>
    );
  }
  if (!flow || !step?.rtmsSequence)
    return (
      <Layout>
        <p role="alert">
          This RTMS sequence is unavailable. Please return to the task.
        </p>
        <Button
          onClick={() =>
            navigate(AppRoutes.taskInstructions, {
              state: { taskId, skipToProgress: true },
            })
          }
        >
          Return to task
        </Button>
      </Layout>
    );
  if (!runReady || !run || accepted !== true)
    return (
      <Layout>
        <p>Preparing sequence...</p>
      </Layout>
    );
  return (
    <SequenceSection
      key={`${taskId}:${step.stepNumber}:${run.id}`}
      flow={flow}
      step={step}
      userId={identity.userId}
      sessionId={identity.sessionId}
      collectionId={identity.collectionId}
      redoMode={Boolean(location.state?.redoMode)}
    />
  );
}

function SequenceSection({
  flow,
  step,
  userId,
  sessionId,
  collectionId,
  redoMode,
}: {
  flow: ResolvedWorkTask;
  step: ResolvedWorkTaskStep;
  userId: string;
  sessionId: string;
  collectionId: string;
  redoMode: boolean;
}) {
  const context = useMultiStepCapture();
  const { addStepDetails } = context;
  const navigate = useNavigate();
  const sequence = step.rtmsSequence!;
  const navigationEntryId = useLocation().state?.navigationEntryId as
    | string
    | undefined;
  const navigationSteps = useMemo(() => {
    if (!navigationEntryId) return undefined;
    try {
      return getTaskNavigationSteps(flow, navigationEntryId);
    } catch {
      return [];
    }
  }, [flow, navigationEntryId]);
  const section = useMemo(() => getRtmsSection(flow, step), [flow, step]);
  const progressSections = useMemo(() => getWorkTaskSections(flow), [flow]);
  const progressSteps = progressSections.map(([first]) => ({
    ...first,
    stepTitle: first.rtmsSequence?.name || first.stepTitle,
    stepLabelDisplayName: first.rtmsSequence
      ? undefined
      : first.stepLabelDisplayName,
  }));
  const taskFields = useMemo(() => getOrbStepDetailFields(step), [step]);
  const instructionGroups = useMemo(() => {
    const groups = new Map<
      number,
      {
        taskName: string;
        slices: ResolvedWorkTaskStep["instructionSlices"];
      }
    >();
    for (const item of section) {
      const group = groups.get(item.captureTaskIndex) || {
        taskName: item.captureTaskName,
        slices: [],
      };
      // General task slices are shared by its numbered RTMS captures.
      group.slices = Array.from(
        new Set([...group.slices, ...item.instructionSlices])
      );
      groups.set(item.captureTaskIndex, group);
    }
    return Array.from(groups.values()).filter((group) => group.slices.length);
  }, [section]);
  const [showSequenceFields, setShowSequenceFields] = useState(
    () =>
      step.stepNumber === sequence.firstStepNumber ||
      validateRtmsValues(
        sequence.fields,
        getRtmsSequenceValues(step, context.stepDetails)
      ).length > 0
  );
  const sequenceFields = showSequenceFields ? sequence.fields : [];
  const needsSetup = Boolean(
    sequenceFields.length ||
      taskFields.length ||
      step.rtmsContextEntry === "ask_before_task"
  );
  const [showQr, setShowQr] = useState(!needsSetup);
  const [redo, setRedo] = useState(redoMode);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [drafts, setDrafts] = useState<StepDetailsData[]>([]);
  const draftRef = useRef(new Map<number, StepDetailsData>());
  const savedDetailsRef = useRef(context.stepDetails);
  savedDetailsRef.current = context.stepDetails;
  const pending = useRef<Promise<void>>(Promise.resolve());
  const sequenceValidation = useRef<() => unknown[]>(() => []);
  const taskValidation = useRef<() => unknown[]>(() => []);
  const details = useMemo(
    () => [
      ...context.stepDetails.filter(
        (item) => !drafts.some((draft) => draft.stepNumber === item.stepNumber)
      ),
      ...drafts,
    ],
    [context.stepDetails, drafts]
  );
  const shared = useMemo(
    () => getStoredWorkTaskSharedDetails(flow, context.stepCaptures),
    [context.stepCaptures, flow]
  );
  const run = getRtmsRun(details)!;
  const knownValues = useMemo(
    () =>
      Object.fromEntries([
        ...getOrbDetailPairs(shared).map((pair) => {
          const separator = pair.indexOf(",");
          return [pair.slice(0, separator), pair.slice(separator + 1)];
        }),
        ...getRtmsSequenceValues(step, details).map(({ label_id, value }) => [
          label_id,
          value,
        ]),
      ]),
    [details, shared, step]
  );
  const labelRelations = useMemo(
    () => [...flow.labelRelations, ...step.labelRelations],
    [flow.labelRelations, step.labelRelations]
  );
  const isComplete = useCallback(
    (number: number) => {
      const selected = flow.steps.find((item) => item.stepNumber === number);
      return Boolean(
        selected &&
          isWorkTaskStepComplete(
            selected,
            context.stepCaptures,
            details,
            shared
          )
      );
    },
    [context.stepCaptures, details, flow, shared]
  );
  const sectionComplete =
    section.every((item) => isComplete(item.stepNumber)) && !redo;
  const allComplete =
    flow.steps.every((item) => isComplete(item.stepNumber)) && !redo;
  const attempt = getRtmsAttempt(
    section,
    context.stepCaptures.filter(
      (capture) => capture.rtmsConfirmation?.runId === run.id
    ),
    redo
  );
  const activeSessionMetadata = useSessionMetadataValues();
  const savedSessionCapture = !redo
    ? context.stepCaptures.find(
        (capture) => capture.stepNumber === step.stepNumber
      )
    : undefined;
  const sessionMetadata = savedSessionCapture
    ? savedSessionCapture.sessionMetadata || {}
    : activeSessionMetadata;
  let qr: ReturnType<typeof buildRtmsSequenceQr> | undefined;
  let qrError = "";
  try {
    qr = buildRtmsSequenceQr({
      flow,
      section,
      run,
      userId,
      sessionId,
      collectionId,
      details,
      shared,
      attempt,
      sessionMetadata,
    });
  } catch (problem) {
    qrError =
      problem instanceof Error
        ? problem.message
        : "The QR could not be generated.";
  }
  const launchId = qr?.launchId;
  useEffect(() => {
    setConfirmed(false);
  }, [launchId]);

  const saveGroup = useCallback(
    (
      fields: PrismicCollectionDetailField[],
      values: NonNullable<StepDetailsData["stepDetailsFields"]>,
      isSequence = false
    ) => {
      // Sequence values keep their existing storage anchor even when setup is
      // opened from a later section. Never tag that anchor as the scanned task.
      const detailsStep = isSequence
        ? flow.steps.find(
            (item) => item.stepNumber === sequence.firstStepNumber
          )!
        : step;
      const previous =
        draftRef.current.get(detailsStep.stepNumber) ||
        savedDetailsRef.current.find(
          (item) => item.stepNumber === detailsStep.stepNumber
        );
      const ids = new Set(fields.map((field) => String(field.label_id)));
      const merged = [
        ...(previous?.stepDetailsFields || []).filter(
          (item) => !ids.has(item.label_id)
        ),
        ...values,
      ];
      const taskValues = isSequence
        ? previous?.stepDetailsFields || []
        : merged;
      const next: StepDetailsData = {
        ...previous,
        stepNumber: detailsStep.stepNumber,
        ...(detailsStep.captureEntryId
          ? { captureEntryId: detailsStep.captureEntryId }
          : {}),
        stepDetailsFields: taskValues,
        stepDetails: taskValues.map(
          (item) => `${item.label_id}: ${item.value}`
        ),
        ...(isSequence
          ? { rtmsSequenceDetails: { sequenceId: sequence.id, fields: values } }
          : {}),
      };
      draftRef.current.set(detailsStep.stepNumber, next);
      setDrafts(Array.from(draftRef.current.values()));
      pending.current = pending.current
        .catch(() => undefined)
        .then(() => addStepDetails(next));
      pending.current.catch(() =>
        setError("Your details could not be saved. Please try again.")
      );
    },
    [addStepDetails, flow.steps, step, sequence.firstStepNumber, sequence.id]
  );
  const onSequenceChange = useCallback(
    (
      _number: number,
      _strings: string[],
      values?: StepDetailsData["stepDetailsFields"]
    ) => saveGroup(sequence.fields, values || [], true),
    [saveGroup, sequence.fields]
  );
  const onTaskChange = useCallback(
    (
      _number: number,
      _strings: string[],
      values?: StepDetailsData["stepDetailsFields"]
    ) => saveGroup(taskFields, values || []),
    [saveGroup, taskFields]
  );
  const getSequenceDetails = useCallback(
    () =>
      getRtmsSequenceValues(step, details).map(
        (item) => `${item.label_id}: ${item.value}`
      ),
    [details, step]
  );
  const getTaskDetails = useCallback(
    () =>
      (
        details.find((item) => item.stepNumber === step.stepNumber)
          ?.stepDetailsFields || []
      )
        .filter((item) =>
          taskFields.some((field) => field.label_id === item.label_id)
        )
        .map((item) => `${item.label_id}: ${item.value}`),
    [details, step.stepNumber, taskFields]
  );
  const registerSequenceValidation = useCallback(
    (validate: () => unknown[]) => {
      sequenceValidation.current = validate;
    },
    []
  );
  const registerTaskValidation = useCallback((validate: () => unknown[]) => {
    taskValidation.current = validate;
  }, []);

  const guard = async (action: () => Promise<void> | void) => {
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      try {
        await pending.current;
      } catch {
        // Retry durable drafts before allowing navigation or a QR confirmation.
        for (const draft of Array.from(draftRef.current.values())) {
          await addStepDetails(draft);
        }
        pending.current = Promise.resolve();
      }
      await action();
    } catch (problem) {
      setError(
        problem instanceof Error
          ? problem.message
          : "Your progress could not be saved. Please retry."
      );
    } finally {
      setBusy(false);
    }
  };
  const goTo = (selected: ResolvedWorkTaskStep) =>
    navigate(getWorkTaskStepRoute(selected), {
      state: getWorkTaskStepRouteState(
        flow,
        selected,
        false,
        navigationSteps?.some((item) => item.stepNumber === selected.stepNumber)
          ? navigationEntryId
          : undefined
      ),
    });
  const nextIncomplete = (captures: StepCaptureData[]) =>
    (navigationSteps || flow.steps).find(
      (item) => !isWorkTaskStepComplete(item, captures, details, shared)
    );
  const continueTask = () => {
    const next = nextIncomplete(context.stepCaptures);
    if (next) goTo(next);
    else if (navigationEntryId) navigate(AppRoutes.home);
    else
      navigate(AppRoutes.multiStepUpload, {
        state: { taskId: flow.workTaskId, taskName: flow.workTaskName },
      });
  };
  const confirmSection = async () => {
    if (!qr || !confirmed) return;
    const completed: StepCaptureData[] = section.map((item) => ({
      stepNumber: item.stepNumber,
      stepLabel: item.stepLabel,
      stepTitle: item.stepTitle,
      numberOfCaptures: item.numberOfCaptures,
      captureTaskId: item.captureTaskId,
      ...(item.captureEntryId ? { captureEntryId: item.captureEntryId } : {}),
      captureTaskStepNumber: item.captureTaskStepNumber,
      captureType: "rtms",
      files: [],
      isComplete: true,
      schemaFingerprint: getWorkTaskStepSchemaFingerprint(item),
      sessionMetadata,
      rtmsConfirmation: {
        fingerprint: getRtmsStepFingerprint(item, details, shared, attempt),
        confirmedAt: Date.now(),
        runId: run.id,
        launchId: qr!.launchId,
        attempt,
      },
    }));
    await context.addStepCaptures(completed);
    setRedo(false);
    setConfirmed(false);
    const merged = [
      ...context.stepCaptures.filter(
        (item) =>
          !completed.some((saved) => item.stepNumber === saved.stepNumber)
      ),
      ...completed,
    ];
    const next = nextIncomplete(merged);
    if (next) goTo(next);
    else if (navigationEntryId) navigate(AppRoutes.home);
    else if (flow.hasUploadSteps)
      navigate(AppRoutes.multiStepUpload, {
        state: { taskId: flow.workTaskId, taskName: flow.workTaskName },
      });
    toast.success("Section marked complete from your confirmation.");
  };

  if (navigationSteps?.length === 0)
    return (
      <Layout>
        <p role="alert">
          This task position is no longer available. Your progress is preserved.
        </p>
        <Button onClick={() => navigate(AppRoutes.home)}>
          Back to campaign
        </Button>
      </Layout>
    );

  return (
    <Layout
      className="space-y-6"
      toolbar={
        <div className="grid gap-3">
          {sectionComplete ? (
            <>
              {allComplete && !flow.hasUploadSteps ? (
                <Button
                  disabled={busy}
                  onClick={() =>
                    guard(async () => {
                      await saveCompletedTaskSteps(
                        flow.workTaskId,
                        collectionId,
                        getCompletedTaskSteps({
                          flow,
                          instructions: null,
                          captureType: "rtms",
                          captures: context.stepCaptures,
                          details,
                        })
                      );
                      await context.clearAllCaptures();
                      navigate(AppRoutes.home);
                    })
                  }
                >
                  Finish Work Task
                </Button>
              ) : (
                <Button disabled={busy} onClick={() => guard(continueTask)}>
                  Continue Work Task
                </Button>
              )}
              <Button
                disabled={busy}
                onClick={() => {
                  setRedo(true);
                  setShowQr(!needsSetup);
                }}
              >
                Repeat this section
              </Button>
            </>
          ) : !showQr ? (
            <Button
              disabled={busy}
              onClick={() =>
                guard(() => {
                  if (
                    sequenceValidation.current().length ||
                    taskValidation.current().length
                  )
                    return;
                  if (qrError) {
                    setError(qrError);
                    return;
                  }
                  setShowQr(true);
                })
              }
            >
              Show sequence QR
            </Button>
          ) : (
            <Button
              disabled={busy || !qr || !confirmed}
              onClick={() => guard(confirmSection)}
            >
              Continue after collector finishes
            </Button>
          )}
          <Button
            disabled={busy}
            onClick={() => guard(() => navigate(AppRoutes.home))}
          >
            Save and exit
          </Button>
        </div>
      }
    >
      <h1 className="text-2xl font-bold">{sequence.name}</h1>
      <StepProgressOverview
        steps={progressSteps}
        unitLabel="sections"
        currentStepNumber={step.stepNumber}
        isStepCompleted={(number) =>
          progressSections
            .find(([first]) => first.stepNumber === number)!
            .every((item) => isComplete(item.stepNumber))
        }
        isStepStarted={(number) =>
          progressSections
            .find(([first]) => first.stepNumber === number)!
            .some(
              (step) =>
                context.stepCaptures.some(
                  (item) => item.stepNumber === step.stepNumber
                ) || details.some((item) => item.stepNumber === step.stepNumber)
            )
        }
        onSelectStep={(selected) =>
          guard(() => goTo(selected as ResolvedWorkTaskStep))
        }
        renderStepDetails={(selected) =>
          selected.stepNumber === step.stepNumber && (
            <ol
              aria-label="Captures in this section"
              className="ml-12 mr-3 list-decimal space-y-2 border-l border-f4f7ff py-3 pl-6 text-sm text-858494"
            >
              {section.map((item) => (
                <li key={item.stepNumber}>
                  {item.captureTaskName}
                  {item.hasCaptureTaskSteps &&
                  item.captureTaskStepTitle !== item.captureTaskName
                    ? ` — ${item.captureTaskStepTitle}`
                    : ""}
                  {flow.steps.filter(
                    (candidate) =>
                      candidate.captureTaskIndex === item.captureTaskIndex
                  ).length > 1
                    ? ` — Step ${item.captureTaskStepNumber}`
                    : ""}
                </li>
              ))}
            </ol>
          )
        }
      />
      {instructionGroups.length > 0 && (
        <DisclosureCard
          title="Instructions"
          description="Guidance for the captures in this section"
        >
          <div className="space-y-5">
            {instructionGroups.map((group, index) => (
              <section key={index}>
                <h2 className="mb-3 text-sm font-semibold">{group.taskName}</h2>
                <SliceZone
                  slices={group.slices as any}
                  components={taskInstructionSliceComponents}
                  context={{ inlineInstructions: true }}
                />
              </section>
            ))}
          </div>
        </DisclosureCard>
      )}
      <RtmsSectionDetails
        sharedDetails={shared}
        sharedFields={flow.sharedCollectionDetailFields}
        sectionGroups={[
          {
            title: "This RTMS sequence",
            fields: sequence.fields,
            values: getRtmsSequenceValues(step, details),
          },
          ...section.map((item) => ({
            title: `${item.captureTaskName} — Step ${item.captureTaskStepNumber}`,
            fields: getOrbStepDetailFields(item),
            values: getRtmsFieldValues(
              getOrbStepDetailFields(item),
              details,
              item.stepNumber
            ),
          })),
        ]}
        disabled={busy}
        onEditShared={() =>
          guard(() =>
            navigate(AppRoutes.taskInstructions, {
              state: {
                taskId: flow.workTaskId,
                editCollectionDetails: true,
                nextRoute: AppRoutes.rtmsSequence,
                stepNumber: step.stepNumber,
                ...(navigationEntryId ? { navigationEntryId } : {}),
              },
            })
          )
        }
        onEditSection={
          showQr && needsSetup
            ? () => {
                setShowQr(false);
                setConfirmed(false);
              }
            : undefined
        }
      />
      {error && (
        <p role="alert" className="text-red-700">
          {error}
        </p>
      )}
      {sectionComplete ? (
        <p>This section is marked complete based on your confirmation.</p>
      ) : (
        <>
          {!showQr && (
            <>
              {sequenceFields.length > 0 && (
                <section aria-label="Sequence details">
                  <StepDetails
                    heading="Details for this RTMS sequence"
                    description="Shared within this RTMS sequence"
                    stepDetailsFields={sequence.fields}
                    knownTaskValues={knownValues}
                    labelRelations={labelRelations}
                    stepNumber={-sequence.firstStepNumber}
                    getStepDetails={getSequenceDetails}
                    onStepDetailsChange={onSequenceChange}
                    onValidationRegister={registerSequenceValidation}
                    disabled={busy}
                  />
                </section>
              )}
              {taskFields.length > 0 && (
                <section aria-label="Task details">
                  <StepDetails
                    heading={`Details for ${step.captureTaskName}`}
                    description="For this task"
                    stepDetailsFields={taskFields}
                    knownTaskValues={knownValues}
                    labelRelations={labelRelations}
                    stepNumber={step.stepNumber}
                    getStepDetails={getTaskDetails}
                    onStepDetailsChange={onTaskChange}
                    onValidationRegister={registerTaskValidation}
                    disabled={busy}
                  />
                </section>
              )}
              {step.rtmsContextEntry === "ask_before_task" && (
                <p>
                  Complete the instructions for this task before showing the
                  next QR.
                </p>
              )}
            </>
          )}
          {showQr && (
            <>
              {qr ? (
                <OrbQRCode
                  content={qr.content}
                  label="RTMS sequence QR code"
                  variant="rtms"
                />
              ) : (
                <p role="alert">{qrError}</p>
              )}
              <p>
                Show this QR to the collector. It runs only the tasks listed
                above. Return here when the collector asks you to continue.
              </p>
              {qrError && step.stepNumber !== sequence.firstStepNumber && (
                <Button
                  onClick={() => {
                    setShowSequenceFields(true);
                    setShowQr(false);
                    setConfirmed(false);
                  }}
                >
                  Enter sequence details
                </Button>
              )}
              <label className="flex items-start gap-3">
                <input
                  type="checkbox"
                  checked={confirmed}
                  disabled={busy || !qr}
                  onChange={(event) => setConfirmed(event.target.checked)}
                />
                <span>
                  The collector has finished every task in this section.
                </span>
              </label>
            </>
          )}
        </>
      )}
    </Layout>
  );
}
